import React, { useState } from "react";
import { Dialog, DialogContent, Divider, Fade, Menu, MenuItem, useTheme } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { Link, withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { DefaultUserAvatar } from "./default-avatar.component";
import { ReactComponent as GermanFlag } from "./../../assets/de.svg";
import { ReactComponent as EnglishFlag } from "./../../assets/en.svg";
import UserImageUpload from "../../features/Settings/partials/user-image-upload.partial";
import { setUserSettings } from "../../utils/axios-requests";
import { EditNotifications, Feedback, InsertPhoto, Logout } from "@mui/icons-material";

/**
 * @description Component that renders the user menu dropdown in the right top corner.
 * @version 0.1.0
 */

const AccountDropdown = () => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const theme = useTheme();

  const context = useSelector((state: RootState) => state.login);
  const { firstName, surname, photoUrl } = useSelector(
    (state: RootState) => state.login.userInformation
  );

  const [profilePicUpload, setProfilePicUpload] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const signOut = () => {
    // TODO: ensure tokens are invalidated on logout - ideally in keycloak provider as reaction on event to ensure all logout calls are covered
    // current behavior: on logout only known email is needed to login with tokens of previous user
    keycloak.logout();
  };

  return (
    <>
      {/* icon displaying the user avatar or initial */}
      <DefaultUserAvatar
        firstName={firstName}
        surname={surname}
        iconUrl={photoUrl}
        onClick={handleClick}
      />

      {/* user setting menu items */}
      <Menu
        id="fade-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        slots={{ transition: Fade }}
        slotProps={{ paper: { sx: { backgroundImage: "unset" } } }}
      >
        {/* profile picture upload */}
        <MenuItem
          data-testid="profile-picture-upload-btn"
          onClick={() => setProfilePicUpload(true)}
        >
          <InsertPhoto sx={{ mr: 1 }} />
          {t("Profile picture")}
        </MenuItem>

        {/* change language */}
        <MenuItem
          data-testid="change-language-setting-btn"
          onClick={() => {
            const updatedSettings = { ...context.settings };
            updatedSettings.language = i18n.language === "en" ? "de" : "en";
            setUserSettings(updatedSettings);
            i18n.changeLanguage(i18n.language === "en" ? "de" : "en");
          }}
        >
          {i18n.language === "de" ? (
            <EnglishFlag style={{ marginLeft: 2, marginRight: 10, width: 20, height: 24 }} />
          ) : (
            <GermanFlag style={{ marginLeft: 2, marginRight: 10, width: 20, height: 24 }} />
          )}

          {t("Switch language")}
        </MenuItem>

        {/* change notification settings */}
        <MenuItem data-testid="change-notification-settings-btn" onClick={() => {}}>
          <Link
            to={"/notification-settings"}
            data-testid={"notificaiton-settings-page-link"}
            style={{
              display: "flex",
              color: theme.palette.text.primary,
              textDecoration: "none"
            }}
          >
            <EditNotifications sx={{ mr: 1 }} />
            {t("Notifications")}
          </Link>
        </MenuItem>

        {/* feedback - not used */}
        <Link
          data-testid="feedback-btn"
          style={{
            color: theme.palette.text.primary,
            textDecoration: "none",
            display: "none"
          }}
          to="/feedback"
          onClick={close}
        >
          <MenuItem>
            <Feedback sx={{ mr: 1 }} />
            {t("Feedback")}
          </MenuItem>
        </Link>

        <Divider sx={{ opacity: "0.6" }} />

        {/* sign out */}
        <Link
          data-testid="sign-out-btn"
          style={{
            color: theme.palette.text.primary,
            textDecoration: "none",
            display: "block"
          }}
          to="/#"
          onClick={signOut}
        >
          <MenuItem>
            <Logout sx={{ mr: 1 }} />
            {t("Sign Out")}
          </MenuItem>
        </Link>
      </Menu>

      {/* user image upload dialog */}
      <Dialog
        onClose={() => setProfilePicUpload(false)}
        open={profilePicUpload}
        slotProps={{ paper: { sx: { backgroundImage: "unset" } } }}
      >
        <DialogContent>
          <UserImageUpload handleClose={() => setProfilePicUpload(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withRouter(AccountDropdown);
